import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
} from "../../icons";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import ListItem from "@mui/material/ListItem";
import { observer } from "mobx-react";
import { useRootStore } from "../../store/contexts";
import { menusByAudience } from "./mainMenu";
import { currentAudience } from "../../audience/audience";
import { Box, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import {
  UnknownIcon,
  HomeIcon,
  MusicNoteIcon,
  PersonIcon,
  MenuBookIcon,
  MicIcon,
  CollectionsBookmarkIcon,
  EditNoteIcon,
  DrawIcon,
  TableChartIcon,
  DescriptionIcon,
  AlbumIcon,
  TravelExploreIcon,
  StorefrontIcon,
  LinkIcon,
  BuildIcon,
  ListAltIcon,
  SearchIcon,
  ChecklistIcon,
  PaymentsIcon,
  TollIcon,
  ShoppingCartIcon,
  ReceiptIcon,
  SettingsIcon,
  BusinessIcon,
  PersonOutlineIcon,
  SmartToyIcon,
  ArticleIcon,
  TokenIcon,
  StorageIcon,
  BadgeIcon,
  GroupIcon,
  FolderSharedIcon,
  SignalWifiStatusbarConnectedNoInternet4Icon,
  DomainIcon,
  TuneIcon,
  GradeIcon,
  SecurityIcon,
  EngineeringOutlinedIcon,
  VpnKeyOutlinedIcon,
  SettingsSuggestIcon,
  BadgeOutlinedIcon,
  HistoryEduIcon,
  CategoryIcon,
  AttachMoneyIcon,
  MonitorIcon,
  DnsIcon,
  AssignmentIcon,
  MonitorHeartIcon,
  EventIcon,
  PrecisionManufacturingIcon,
  InsightsIcon,
  ScienceIcon,
  DashboardIcon,
  WidgetsIcon,
  VpnKeyIcon,
  ViewQuiltIcon,
  AccountTreeIcon,
  PsychologyIcon,
  ChatIcon,
  PatternIcon,
  QuestionAnswerIcon,
} from "../../icons";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

type GridExpressProps = {
  open: boolean;
  onClose: () => void;
  headerHeight?: number;
};

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const iconByName: Record<string, React.ReactElement> = {
  Home: <HomeIcon />,
  MusicNote: <MusicNoteIcon />,
  Person: <PersonIcon />,
  MenuBook: <MenuBookIcon />,
  Mic: <MicIcon />,
  CollectionsBookmark: <CollectionsBookmarkIcon />,
  EditNote: <EditNoteIcon />,
  Draw: <DrawIcon />,
  TableChart: <TableChartIcon />,
  Description: <DescriptionIcon />,
  Album: <AlbumIcon />,
  TravelExplore: <TravelExploreIcon />,
  Storefront: <StorefrontIcon />,
  Link: <LinkIcon />,
  Build: <BuildIcon />,
  ListAlt: <ListAltIcon />,
  Search: <SearchIcon />,
  Checklist: <ChecklistIcon />,
  Payments: <PaymentsIcon />,
  Toll: <TollIcon />,
  ShoppingCart: <ShoppingCartIcon />,
  Receipt: <ReceiptIcon />,
  Settings: <SettingsIcon />,
  Business: <BusinessIcon />,
  PersonOutline: <PersonOutlineIcon />,
  SmartToy: <SmartToyIcon />,
  Article: <ArticleIcon />,
  Token: <TokenIcon />,
  Storage: <StorageIcon />,
  Badge: <BadgeIcon />,
  Group: <GroupIcon />,
  FolderShared: <FolderSharedIcon />,
  SignalWifiStatusbarConnectedNoInternet4: (
    <SignalWifiStatusbarConnectedNoInternet4Icon />
  ),
  Domain: <DomainIcon />,
  Tune: <TuneIcon />,
  Grade: <GradeIcon />,
  Security: <SecurityIcon />,
  EngineeringOutlined: <EngineeringOutlinedIcon />,
  VpnKeyOutlined: <VpnKeyOutlinedIcon />,
  SettingsSuggest: <SettingsSuggestIcon />,
  BadgeOutlined: <BadgeOutlinedIcon />,
  HistoryEdu: <HistoryEduIcon />,
  Category: <CategoryIcon />,
  AttachMoney: <AttachMoneyIcon />,
  Monitor: <MonitorIcon />,
  Dns: <DnsIcon />,
  Assignment: <AssignmentIcon />,
  MonitorHeart: <MonitorHeartIcon />,
  Event: <EventIcon />,
  PrecisionManufacturing: <PrecisionManufacturingIcon />,
  Insights: <InsightsIcon />,
  Science: <ScienceIcon />,
  Dashboard: <DashboardIcon />,
  Widgets: <WidgetsIcon />,
  VpnKey: <VpnKeyIcon />,
  ViewQuilt: <ViewQuiltIcon />,
  AccountTree: <AccountTreeIcon />,
  Psychology: <PsychologyIcon />,
  Chat: <ChatIcon />,
  Pattern: <PatternIcon />,
  QuestionAnswer: <QuestionAnswerIcon />,
};

const getIcon = (name: string) => {
  // El escape de texto literal: el menu puede pedir un texto en vez de un
  // icono. Va PRIMERO y separado del caso de error — mezclarlos hacia que un
  // nombre mal escrito pintara el nombre sin su primera letra, en silencio.
  if (name.startsWith("#")) {
    return name.slice(1);
  }
  const result = iconByName[name];
  if (!result) {
    return <UnknownIcon />;
  }
  return result;
};

// Convert PascalCase projection name to kebab-case route path
// e.g., "SiteUser" -> "site-user"
const projectionToRoutePath = (projectionName: string): string => {
  return projectionName
    .replace(/([A-Z])/g, "-$1")
    .toLowerCase()
    .replace(/^-/, "");
};

// Build the target URL for a menu item, handling projections
const buildTargetUrl = (item: any): string => {
  if (!item.projection) {
    return item.url;
  }
  // When projection is specified, replace the entity path with projection path
  // e.g., /user/list with projection=SiteUser -> /site-user/list
  const projectionPath = projectionToRoutePath(item.projection);
  // URL format is /entity/action (e.g., /user/list, /user/create)
  const urlParts = item.url.split("/").filter(Boolean);
  if (urlParts.length >= 2) {
    // Replace entity name with projection path
    urlParts[0] = projectionPath;
    return "/" + urlParts.join("/");
  }
  return item.url;
};

export const SideBar: React.FC<GridExpressProps> = observer(
  ({ open, onClose, headerHeight = undefined }) => {
    const theme = useTheme();
    const rootStore = useRootStore();
    const { authStore, prefStore, langStore, router } = rootStore;

    React.useEffect(() => {
      const element: HTMLElement | null = document.getElementById(
        prefStore.lastClickedMenu
      );
      if (element) {
        element.scrollIntoView();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only scroll to last menu; should not re-scroll on subsequent menu clicks
    }, []);

    const fontColor = theme.palette.text.primary;

    const currentPathname = router.pathname;

    const singleLevel = (
      item: any,
      index: number,
      isChild: boolean = false
    ) => {
      const label = item.labels[langStore.language].s;
      // Build URL using projection route if specified
      const targetUrl = buildTargetUrl(item);
      const color =
        targetUrl === currentPathname ? theme.palette.primary.main : undefined;
      const listItem = (
        <ListItem
          dense={true}
          sx={{ color }}
          id={item.id}
          onClick={() => {
            // NavigationGuard maneja la verificación de cambios sin guardar
            prefStore.setLastClickedMenu(item.id);
            rootStore.router.goTo(targetUrl);
          }}
        >
          <ListItemButton dense={true} disableGutters={!isChild}>
            <ListItemIcon sx={{ color }}>{getIcon(item.icon)}</ListItemIcon>
            <ListItemText primary={open ? label : ""} />
          </ListItemButton>
        </ListItem>
      );
      if (!prefStore.sideBarOpen) {
        return (
          <Tooltip key={index} title={label} placement="right" arrow>
            {listItem}
          </Tooltip>
        );
      }
      return <div key={index}>{listItem}</div>;
    };

    const multiLevel = (
      item: any,
      index: number,
      isChild: boolean = false,
      upward: boolean = false
    ) => {
      const label = item.labels[langStore.language].s;
      const listItem = (
        <ListItem
          dense={true}
          key={item.id}
          onClick={() => {
            prefStore.toggleMenuState(item.id);
          }}
        >
          <ListItemButton dense={true} disableGutters={!isChild}>
            <ListItemIcon>{getIcon(item.icon)}</ListItemIcon>
            <ListItemText primary={open ? label : ""} />
          </ListItemButton>
          {open && prefStore.getMenuState(item.id) && <ExpandLessIcon />}
          {open && !prefStore.getMenuState(item.id) && <ExpandMoreIcon />}
        </ListItem>
      );
      let isEmpty = true;
      const header = (
        <>
          {prefStore.sideBarOpen && listItem}
          {!prefStore.sideBarOpen && (
            <Tooltip title={label} placement="right" arrow>
              {listItem}
            </Tooltip>
          )}
        </>
      );
      const children = (
        <Collapse
          in={prefStore.getMenuState(item.id)}
          timeout="auto"
          unmountOnExit
        >
          <List component="div">
            {item.items.map((it: any, index: number) => {
              const hasAccess = authStore.hasPermissions(
                it.permission,
                it.roles
              );
              if (hasAccess) {
                isEmpty = false;
                return it.items
                  ? multiLevel(it, index, true)
                  : singleLevel(it, index, true);
              } else {
                return <div key={index}></div>;
              }
            })}
          </List>
        </Collapse>
      );
      // upward (grupos .bottom): los hijos se renderizan ARRIBA del header,
      // asi el grupo anclado al fondo del drawer se expande hacia arriba.
      const result = (
        <div key={index}>
          {upward ? children : header}
          {upward ? header : children}
        </div>
      );
      if (!isEmpty) {
        return result;
      }
      return <div />;
    };

    const listItems: any[] = [];
    const bottomListItems: any[] = [];
    const items = authStore.me
      ? menusByAudience[currentAudience()] ?? []
      : null;

    if (!authStore.me) {
      return null;
    }

    if (items) {
      items.forEach((x: any, index: number) => {
        if (x.icon === "__divider__") {
          // Un divider con .roles/.permission solo se muestra si el usuario tiene
          // acceso — sin esto quedaría un separador huérfano cuando las secciones
          // que agrupa se filtran todas. Sin roles/permission, hasPermissions
          // devuelve true (retrocompatible: el divider se muestra siempre).
          if (authStore.hasPermissions(x.permission, x.roles)) {
            listItems.push(
              <Divider
                key={index}
                sx={{ color: fontColor, paddingTop: 2 }}
                textAlign="left"
              >
                {x.labels
                  ? open
                    ? x.labels[langStore.language].p
                    : x.labels[langStore.language].s
                  : ""}
              </Divider>
            );
          }
        } else if (x.items) {
          if (x.bottom) {
            bottomListItems.push(multiLevel(x, index, false, true));
          } else {
            listItems.push(multiLevel(x, index));
          }
        } else {
          // Check permissions and roles for single-level items
          const hasAccess = authStore.hasPermissions(x.permission, x.roles);
          if (hasAccess) {
            listItems.push(singleLevel(x, index));
          }
        }
      });
    }

    // const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    //   prefStore.setSideBarScrollPos(event.currentTarget.scrollTop);
    // };

    return (
      <Drawer
        variant="permanent"
        open={open}
        PaperProps={
          {
            //onScroll: handleScroll
          }
        }
      >
        <DrawerHeader style={{ height: headerHeight, minHeight: headerHeight }}>
          <IconButton onClick={onClose}>
            {open ? (
              theme.direction === "rtl" ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )
            ) : (
              <div />
            )}
          </IconButton>
        </DrawerHeader>

        <Divider />
        {listItems}
        <Divider />

        <Box sx={{ flexGrow: 1 }} />
        {bottomListItems.length > 0 && (
          <>
            <Divider />
            {bottomListItems}
          </>
        )}
      </Drawer>
    );
  }
);
